<template lang="pug">
TicketView(
  :ticket="props.ticket",
  :useAuth="useTicketManagementAuth",
  :ticket-template-query="{ type: 'tm.ticket.maintenance.*' }",
  :property-restrictions="propertyRestrictions",
  :preview="props.preview"
)
  template(#additionalHeaderFields)
    .tm-item(v-if="isPropertyVisible(maintenanceTicketProps.asset)")
      .smx-label {{ $t("asset") }}
      el-popover(
        trigger="click",
        :visible="assetPopoverVisible",
        :disabled="!isAssetEditable",
        width="300"
      )
        template(#reference)
          .flex.flex-row.items-center(@click="showAssetPopover")
            TwinField(:twin="assetValue")
            el-icon.cursor-pointer.ml-4.edit-icon(
              v-if="isAssetEditable",
              @click="showAssetPopover"
            )
              Edit
        .flex-col
          TwinSelect(
            v-model="asset",
            :multiple="false",
            :query="{ isTemplate: false, type: productionPrefix + '*', active: true }",
            :props-in-label="['location', 'serialNumber']",
            show-details
          )
          .flex.justify-end.mt-4
            el-button(type="danger", @click="assetPopoverVisible = false") {{ $t("cancel") }}
            el-button(
              v-loading="assetLoading",
              type="primary",
              @click="saveAsset"
            ) {{ $t("save") }}

    .tm-item(v-if="isPropertyVisible(maintenanceTicketProps.area)")
      .smx-label {{ $t("location") }}
      .flex.flex-row
        EditableText(
          :content="location",
          :save-callback="saveLocation",
          :disabled="!isLocationEditable",
          :disable-empty="true"
        )
          span {{ propValuesByCustomId.area }}

    .tm-item(v-if="isPropertyVisible(maintenanceTicketProps.responsibility)")
      .smx-label {{ $t("responsibility") }}
      el-popover(
        trigger="click",
        :disabled="!isResponsibilityEditable",
        :visible="responsibilityPopoverVisible",
        width="300"
      )
        .flex-col
          ValueListSelect(
            v-model="responsibility",
            custom-id="maintenanceResponsibility",
            hide-editing
          )
          .flex.justify-end.mt-4
            el-button(
              type="danger",
              @click="responsibilityPopoverVisible = false"
            ) {{ $t("cancel") }}
            el-button(
              v-loading="responsibilityLoading",
              type="primary",
              @click="saveResponsibility"
            ) {{ $t("save") }}
        template(#reference)
          .flex.flex-row.items-center.cursor-pointer(
            @click="showResponsibilityPopover"
          )
            span.mr-4 {{ propValuesByCustomId.responsibility }}
            el-icon(v-if="isResponsibilityEditable")
              Edit

    .tm-item(
      v-if="isPropertyVisible(maintenanceTicketProps.causeCategory) && propValuesByCustomId.maintenanceRequestType === 'damageNotice'"
    )
      .smx-label {{ $t("causeCategory") }}
      el-tag.max-w-32(
        v-if="propValuesByCustomId.causeCategory",
        type="primary"
      ) {{ propValuesByCustomId.causeCategory }}
  template(#additionalFields)
    .ticket-material.mb-8
      MaterialUse(:ticket="ticket")
</template>

<script setup lang="ts">
import { type TwinResponse } from "@shared/services/twinManagement";
import { useTicketManagementMenu } from "../hooks/maintenanceMenu";
import { useTicketManagementAuth } from "../hooks/maintenanceAuth";
import MaterialUse from "./MaterialUse.vue";
import TicketView, {
  type TicketPropertyRestriction,
} from "@shared/components/tickets/TicketView.vue";
import { computed, ref } from "vue";
import { maintenanceTicketProps } from "../constants";
import { useTwinStore } from "@shared/stores/twinManagement";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { useI18n } from "vue-i18n";
import { prefix as productionPrefix } from "@shared/assetManagement/constants";
import { statuses } from "@shared/components/tickets/constants";
import TwinField from "@shared/components/twinManagement/components/TwinField.vue";
import EditableText from "@shared/components/elements/EditableText.vue";
import TwinSelect from "@shared/components/twinManagement/components/TwinSelect.vue";
import ValueListSelect from "@shared/components/twinManagement/components/ValueListSelect.vue";
import { useMachineRestrictionStore } from "../machineRestrictions/store";

const props = defineProps<{
  ticket: TwinResponse;
  preview?: boolean;
}>();

useTicketManagementMenu();

const { t } = useI18n();

const propValuesByCustomId = computed(() => {
  const o: Record<string, unknown> = {};
  props.ticket.properties?.forEach((prop) => {
    o[prop.customId!] = prop.propertyValue?.value;
  });
  return o;
});

const assetValue = computed(() => {
  const value = propValuesByCustomId.value[maintenanceTicketProps.asset];
  return Array.isArray(value)
    ? (value[0] as TwinResponse | undefined)
    : undefined;
});

const location = computed(() => {
  const value = propValuesByCustomId.value[maintenanceTicketProps.area];
  return typeof value === "string" ? value : "";
});

const twinStore = useTwinStore();
const machineRestrictionStore = useMachineRestrictionStore();

const propertyRestrictions = computed<
  Record<string, TicketPropertyRestriction>
>(() => {
  const machineRestriction = machineRestrictionStore.result?.find(
    (item) =>
      item.templateId === props.ticket.templateUuid ||
      item.templateId === props.ticket.type,
  );
  return Object.fromEntries(
    (machineRestriction?.propertyDefaults ?? []).map((restriction) => [
      restriction.customId,
      { hidden: restriction.hidden, locked: restriction.locked },
    ]),
  );
});

function isPropertyVisible(customId: string) {
  return !propertyRestrictions.value[customId]?.hidden;
}

function isPropertyLocked(customId: string) {
  return Boolean(propertyRestrictions.value[customId]?.locked);
}

const auth = useTicketManagementAuth();

const status = computed(() => {
  return props.ticket.properties?.find((item) => item.customId === "status")
    ?.propertyValue?.value as string;
});

const isBeforeCompleted = computed(() => {
  return [
    statuses.draft,
    statuses.planned,
    statuses.new,
    statuses.inProcess,
  ].includes(status.value);
});

// asset
const asset = ref<TwinResponse[] | null>(null);
const assetLoading = ref(false);
const assetPopoverVisible = ref(false);

const isAssetEditable = computed(() => {
  return (
    (auth.isAdmin() || auth.isTicketTypeManager(props.ticket)) &&
    !isPropertyLocked(maintenanceTicketProps.asset) &&
    isBeforeCompleted.value
  );
});

function showAssetPopover() {
  if (!isAssetEditable.value) {
    return;
  }
  asset.value = propValuesByCustomId.value?.["asset"] as TwinResponse[] | null;
  assetPopoverVisible.value = true;
}

async function saveAsset() {
  if (isPropertyLocked(maintenanceTicketProps.asset)) {
    return;
  }
  const value = asset.value?.[0]?.uuid ? asset.value?.[0]?.uuid : [];

  const propertyUuid = props.ticket.properties!.find(
    (item) => item.customId === maintenanceTicketProps.asset,
  )?.uuid;
  if (propertyUuid === undefined) {
    throw new Error("UUID for asset property not found");
  }

  assetLoading.value = true;
  await twinStore
    .editTwin(props.ticket, {
      properties: [
        {
          propertyUuid,
          value: value,
        },
      ],
    })
    .then(
      () => {},
      (e) => {
        showResponseErrorToUser(e, t);
      },
    )
    .finally(() => {
      assetLoading.value = false;
    });
  assetPopoverVisible.value = false;
}

// Responsibility
const responsibility = ref<string | null>(null);
const responsibilityLoading = ref(false);
const responsibilityPopoverVisible = ref(false);

const isResponsibilityEditable = computed(
  () =>
    !isPropertyLocked(maintenanceTicketProps.responsibility) &&
    ![statuses.completed, statuses.confirmed].includes(status.value),
);

function showResponsibilityPopover() {
  if (!isResponsibilityEditable.value) {
    return;
  }
  responsibility.value = propValuesByCustomId.value["responsibility"] as string;
  responsibilityPopoverVisible.value = true;
}

async function saveResponsibility() {
  if (isPropertyLocked(maintenanceTicketProps.responsibility)) {
    return;
  }
  if (responsibility.value) {
    responsibilityLoading.value = true;
    const propertyUuid = props.ticket.properties!.find(
      (item) => item.customId === maintenanceTicketProps.responsibility,
    )?.uuid;
    if (propertyUuid === undefined) {
      throw new Error("UUID for responsibility property not found");
    }
    await twinStore
      .editTwin(props.ticket, {
        properties: [
          {
            propertyUuid,
            value: responsibility.value,
          },
        ],
      })
      .then(
        () => {},
        (e) => {
          showResponseErrorToUser(e, t);
        },
      )
      .finally(() => {
        responsibilityLoading.value = false;
      });
  }
  responsibilityPopoverVisible.value = false;
}

// Area

const isLocationEditable = computed(() => {
  return (
    (auth.isAdmin() || auth.isTicketTypeManager(props.ticket)) &&
    !isPropertyLocked(maintenanceTicketProps.area) &&
    isBeforeCompleted.value
  );
});

async function saveLocation(value: string) {
  if (isPropertyLocked(maintenanceTicketProps.area)) {
    return;
  }
  const propertyUuid = props.ticket.properties!.find(
    (item) => item.customId === maintenanceTicketProps.area,
  )?.uuid;
  if (propertyUuid === undefined) {
    throw new Error("UUID for area property not found");
  }
  await twinStore.editTwin(props.ticket, {
    properties: [
      {
        propertyUuid,
        value,
      },
    ],
  });
}
</script>
